import React, { FC, useEffect, useState } from "react";
import cn from "clsx";
import Modal from "react-modal";
import crossIcon from "@/images/cross_black_icon.svg";
import { isMobile } from "@/util";
import { useCurrency, useCountryShort, useIsRTL, useLanguage } from "@/hooks";
import HtmlParser from "@/components/html-parser";
import PdpAnalyticsEvent from "@/services/analytics/main/pdp";
import InfoIconGrey from "@/images/info-icon-grey.svg";
import "./style.scss";

type PayLaterOptionItemProps = {
  paymentService?: any;
  paymentSystemIcon: string;
  normalPrice: string | number;
  paymentMode: string;
  payIconClassName?: string;
  optionMessages: string[];
  additionalUrl?: string;
};

const baseModalContent = {
  top: "50%",
  left: "50%",
  right: "auto",
  bottom: "auto",
  marginRight: "-50%",
  transform: "translate(-50%, -50%)",
  width: isMobile.any() ? "90%" : "50%",
  padding: 0,
  border: "none",
  borderRadius: "8px",
  background: "#fff"
};

const imageModalStyles = {
  content: {
    ...baseModalContent,
    maxHeight: "80vh",
    overflowY: "auto" as const
  },
  overlay: { zIndex: 100 }
};

const iframeModalStyles = {
  content: { ...baseModalContent, height: "65%" },
  overlay: { zIndex: 100 }
};

const IMAGE_PATTERN = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;

const resolveUrl = (
  url: string,
  language: string,
  amount: string | number,
  currency: string,
  countryShort: string
): string =>
  url
    .replace(/api__language__/g, language)
    .replace(/api__amount__/g, String(amount))
    .replace(/api__currency__/g, currency)
    .replace(/api__countryShort__/g, countryShort.toUpperCase());

const PayLaterOptionItem: FC<PayLaterOptionItemProps> = ({
  paymentService,
  paymentSystemIcon,
  normalPrice,
  paymentMode,
  payIconClassName,
  optionMessages,
  additionalUrl
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const languageSlug = useLanguage();
  const isRTL = useIsRTL();
  const currencyCode = useCurrency();
  const countryShort = useCountryShort();
  useEffect(() => {
    if (paymentService) {
      paymentService.loadSdk();
    }
  }, []);

  useEffect(() => {
    if (paymentService && paymentService.isSdkLoaded) {
      paymentService.init({});
    }
  }, []);

  const handleInfoIconClick = () => {
    setModalOpen(true);
    PdpAnalyticsEvent.paymentMethodInfo({ method: paymentMode });
  };

  const resolvedUrl = additionalUrl
    ? resolveUrl(
        additionalUrl,
        languageSlug,
        normalPrice,
        currencyCode,
        countryShort
      )
    : undefined;
  const isImageUrl = additionalUrl && IMAGE_PATTERN.test(additionalUrl);

  const closeButton = (
    <div
      style={{
        position: "sticky",
        top: 0,
        display: "flex",
        justifyContent: "flex-end",
        padding: "10px 12px",
        background: "#fff",
        zIndex: 2
      }}
    >
      <img
        src={crossIcon}
        alt="close"
        style={{ cursor: "pointer", width: 12, height: 12 }}
        onClick={() => setModalOpen(false)}
      />
    </div>
  );

  return (
    <div
      className={cn("price-snippet", paymentMode.toLowerCase(), {
        arabic: isRTL
      })}
    >
      <div className="description">
        {optionMessages.map((message: string, index: number) => (
          <HtmlParser html={message} key={index} />
        ))}
      </div>

      <div
        className={cn("pay-icon", paymentMode.toLowerCase(), payIconClassName)}
      >
        <div className="position-relative">
          <img
            src={paymentSystemIcon}
            alt="Payment System Icon"
            className="svg-icon payment-system-icon"
          />
          {additionalUrl && (
            <div
              className="info-icon d-none-resp"
              onClick={handleInfoIconClick}
            >
              <img src={InfoIconGrey} alt="info" />
            </div>
          )}
        </div>
        <div
          className="touch-detector"
          data-tabby-info="installments"
          data-tabby-language={languageSlug}
        />
      </div>

      {modalOpen && resolvedUrl && (
        <Modal
          isOpen={modalOpen}
          style={isImageUrl ? imageModalStyles : iframeModalStyles}
          onRequestClose={() => setModalOpen(false)}
        >
          {closeButton}
          {isImageUrl ? (
            <img
              src={resolvedUrl}
              alt="Payment info"
              style={{ width: "100%", display: "block" }}
            />
          ) : (
            <iframe
              width="100%"
              height="100%"
              src={resolvedUrl}
              style={{ border: "none", display: "block" }}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default PayLaterOptionItem;
